/** Keys typed within this window build one search ("P" then "E" → "PE"); a pause starts over. */
export const TYPE_AHEAD_RESET_MS = 700;

/** What a closed select has typed so far, and when the last key came. */
export interface TypeAheadBuffer {
  text: string;
  at: number;
}

interface TypeAheadKey {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

/** A printable character typed without a shortcut modifier: the keys a select searches by. */
export const isTypeAheadKey = ({ key, ctrlKey, metaKey, altKey }: TypeAheadKey): boolean =>
  key.length === 1 && key.trim() !== '' && !ctrlKey && !metaKey && !altKey;

/**
 * The key came from a select's closed combobox. An open select's menu searches by itself, and its
 * keys also bubble through the portal to the field, so they are left alone.
 */
export const isClosedSelectTarget = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  target.getAttribute('role') === 'combobox' &&
  target.getAttribute('aria-expanded') !== 'true';

/** Upper case without accents, so "sao" finds "São Paulo". */
const foldText = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();

/** How an option is searched: by its own text, or by several (a state's name and its UF). */
export type TypeAheadKeys<Option extends string> = (option: Option) => readonly string[];

const ownText = <Option extends string>(option: Option) => [option];

/** The first option with a search text starting with the text, ignoring case and accents. */
export const findTypeAheadOption = <Option extends string>(
  options: readonly Option[],
  text: string,
  getKeys: TypeAheadKeys<Option> = ownText,
): Option | null => {
  const search = foldText(text);
  if (!search) return null;
  return (
    options.find((option) => getKeys(option).some((key) => foldText(key).startsWith(search))) ??
    null
  );
};

/**
 * One key typed on a closed select: it adds to the search when it comes within
 * `TYPE_AHEAD_RESET_MS` of the previous one, and the select jumps to the first option starting
 * with the search. When the longer search matches nothing ("P", "X"), the key starts a new search
 * on its own.
 */
export const nextTypeAhead = <Option extends string>(
  options: readonly Option[],
  previous: TypeAheadBuffer | null,
  key: string,
  now: number,
  getKeys: TypeAheadKeys<Option> = ownText,
): { buffer: TypeAheadBuffer; option: Option | null } => {
  const continues = previous !== null && now - previous.at <= TYPE_AHEAD_RESET_MS;
  const text = continues ? previous.text + key : key;
  const option = findTypeAheadOption(options, text, getKeys);
  if (option || !continues) return { buffer: { text, at: now }, option };
  return { buffer: { text: key, at: now }, option: findTypeAheadOption(options, key, getKeys) };
};
